import React, { useState } from 'react';

const WorksheetDetailDrawer = ({
  isOpen,
  onClose,
  detail,
  isLoading,
  onStartWork,
  onHoldWork,
  onResumeWork,
  onRepeatWork,
  onRejectWork,
  onCompleteWork,
  onPrintSingle
}) => {
  // Action Modals State (Hold, Repeat, Reject)
  const [activeModal, setActiveModal] = useState(null); // 'hold' | 'repeat' | 'reject'
  const [selectedReason, setSelectedReason] = useState('');
  const [actionRemarks, setActionRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleCloseActionModal = () => {
    setActiveModal(null);
    setSelectedReason('');
    setActionRemarks('');
  };

  const handleSubmitAction = async () => {
    if (!selectedReason && activeModal !== 'resume') {
      alert('Please select a valid reason.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (activeModal === 'hold') {
        await onHoldWork(detail.testId, selectedReason, actionRemarks);
      } else if (activeModal === 'repeat') {
        await onRepeatWork(detail.testId, selectedReason, actionRemarks);
      } else if (activeModal === 'reject') {
        await onRejectWork(detail.testId, selectedReason, actionRemarks);
      }
      handleCloseActionModal();
    } catch (err) {
      console.error('Action failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ws-drawer-overlay" onClick={onClose}>
      <div className="ws-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Drawer Header */}
        <div className="ws-drawer-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                Workbench Detail
              </span>
              <span className="ws-badge" style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}>
                {detail?.testName || 'Specimen'}
              </span>
              {detail?.priority === 'STAT' && <span className="ws-badge badge-stat">⚡ STAT</span>}
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
              Lab No: <strong>{detail?.labNo}</strong> • Barcode: <strong>{detail?.barcodeNo || detail?.sampleNo}</strong>
            </div>
          </div>
          <button
            type="button"
            className="ws-btn ws-btn-outline ws-btn-sm"
            onClick={onClose}
            title="Close Drawer"
          >
            ✕ Close
          </button>
        </div>

        {/* Drawer Body */}
        <div className="ws-drawer-body">
          {isLoading ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔄</div>
              <div>Loading specimen details &amp; laboratory observations...</div>
            </div>
          ) : !detail ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              No detail found for this specimen.
            </div>
          ) : (
            <>
              {/* Context Normalizer */}
              {(() => {
                const ctx = detail.context || detail;
                return (
                  <>
                    {/* Patient Demographics Summary Strip */}
                    <div className="ws-patient-strip" style={{ marginBottom: '14px' }}>
                      <div className="ws-strip-item">
                        <span className="ws-strip-label">Patient Demographics</span>
                        <span className="ws-strip-value">{ctx.patientName}</span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          UHID: {ctx.patientId} • {ctx.ageGender || `${ctx.ageYear || ctx.age || '-'}Y / ${ctx.gender || '-'}`}
                          {ctx.mobile && ` • 📱 ${ctx.mobile}`}
                        </span>
                      </div>
                      <div className="ws-strip-item">
                        <span className="ws-strip-label">Test &amp; Section</span>
                        <span className="ws-strip-value">{ctx.testName}</span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {ctx.sectionName} • {ctx.sampleType || 'EDTA Whole Blood'}
                        </span>
                      </div>
                      <div className="ws-strip-item">
                        <span className="ws-strip-label">Work Status</span>
                        <span className="ws-strip-value" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span className={`ws-badge ${ctx.status === 'Completed' ? 'badge-completed' : ctx.status === 'In Progress' ? 'badge-in-progress' : ctx.status === 'Hold' ? 'badge-hold' : 'badge-routine'}`}>
                            {ctx.status || 'Pending'}
                          </span>
                        </span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          Stage: <strong>{ctx.sampleStage || 'RECEIVED'}</strong>
                        </span>
                      </div>
                      <div className="ws-strip-item">
                        <span className="ws-strip-label">Turnaround Time (TAT)</span>
                        <span className="ws-strip-value" style={{ color: ctx.tatRemainingMinutes <= 0 ? '#dc2626' : '#0f172a' }}>
                          {ctx.tatRemainingMinutes != null ? `${ctx.tatRemainingMinutes}m remaining` : '-'}
                        </span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          Target: {ctx.isUrgent ? '45m (STAT)' : '180m (Routine)'}
                        </span>
                      </div>
                    </div>

                    {/* Chain of Custody: Collection & Lab Reception Details Card */}
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>🩸</span> Specimen Tracking &amp; Chain of Custody
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                        {/* Sample Collection Box */}
                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 12px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                            1. Sample Collection
                          </div>
                          <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 700, color: ctx.sampleCollectionDate ? '#0f172a' : '#dc2626' }}>
                            {ctx.sampleCollectionDate
                              ? new Date(ctx.sampleCollectionDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : '🔴 Draw Pending'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                            Collected By: <strong>{ctx.sampleCollector || (ctx.sampleCollectionDate ? 'Phlebotomy Desk' : 'Not Collected Yet')}</strong>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            Container: <strong>{ctx.sampleType || 'EDTA Tube'}</strong>
                          </div>
                        </div>

                        {/* Sample Receive Box */}
                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 12px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                            2. Lab Reception
                          </div>
                          <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 700, color: ctx.sampleReceiveDate ? '#0f172a' : '#d97706' }}>
                            {ctx.sampleReceiveDate
                              ? new Date(ctx.sampleReceiveDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                              : (ctx.sampleCollectionDate ? '🟡 In Transit to Lab' : '⚪ Awaiting Draw')}
                          </div>
                          <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                            Received By: <strong>{ctx.sampleReceiver || (ctx.sampleReceiveDate ? 'Central Lab Accession' : '-')}</strong>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            Accession Lab No: <strong>{ctx.labNo}</strong>
                          </div>
                        </div>

                        {/* Testing Analyzer / Bench Box */}
                        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 12px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                            3. Analyzer / Bench Assignment
                          </div>
                          <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 700, color: '#0284c7' }}>
                            ⚙️ {ctx.analyzerName || 'Manual / Bench'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                            Technician: <strong>{ctx.assignedTechnicianName || 'Unassigned'}</strong>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                            Registration: <strong>{ctx.registrationDate ? new Date(ctx.registrationDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status Alert if on Hold */}
                    {ctx.status === 'Hold' && (
                      <div className="ws-alert-delta" style={{ background: '#fffbeb', borderLeftColor: '#d97706', marginBottom: '14px' }}>
                        <span>⏸️</span>
                        <div>
                          <strong>Specimen is on Hold:</strong> {ctx.holdReason || 'Awaiting review'}
                          {ctx.holdRemarks && <div style={{ fontSize: '11px' }}>Notes: {ctx.holdRemarks}</div>}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Parameters / Laboratory Observations Table */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', color: '#0f172a', fontWeight: 700 }}>
                    Parameters &amp; Clinical Ranges ({detail.observations?.length || 0})
                  </h4>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Section: <strong>{detail.sectionName}</strong>
                  </span>
                </div>

                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <table className="ws-params-table">
                    <thead>
                      <tr>
                        <th>Parameter</th>
                        <th style={{ width: '100px' }}>Value</th>
                        <th style={{ width: '80px' }}>Unit</th>
                        <th>Reference Range</th>
                        <th style={{ width: '80px' }}>Flag</th>
                        <th>Previous Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const obsList = detail.parameters || detail.observations || [];
                        if (obsList.length === 0) {
                          return (
                            <tr>
                              <td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                                No test parameters configured for this profile.
                              </td>
                            </tr>
                          );
                        }
                        return obsList.map((obs) => {
                          const isCritical = obs.isCritical || obs.isCriticalAlert;
                          const hasDeltaAlert = obs.hasDeltaAlert || obs.isDeltaAlert;
                          const obsKey = obs.observationId || obs.labObservationId || obs.parameterName;

                          return (
                            <tr
                              key={obsKey}
                              style={{ background: isCritical ? '#fef2f2' : undefined }}
                            >
                              <td>
                                <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                  {obs.parameterName}
                                </div>
                                {obs.methodName && (
                                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                                    Method: {obs.methodName}
                                  </div>
                                )}
                              </td>
                              <td>
                                <span style={{ fontWeight: 700, color: isCritical ? '#dc2626' : '#0f172a' }}>
                                  {obs.currentValue || (
                                    <span style={{ color: '#94a3b8', fontStyle: 'italic', fontWeight: 400 }}>
                                      Pending
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td style={{ color: '#475569' }}>{obs.readingFormat || '-'}</td>
                              <td style={{ fontSize: '12px', color: '#475569' }}>
                                {obs.minRange || obs.maxRange ? `${obs.minRange || 0} - ${obs.maxRange || ''}` : obs.displayReading || 'Normal'}
                              </td>
                              <td>
                                {isCritical ? (
                                  <span className="ws-badge badge-stat" title="CRITICAL ALERT LIMIT">
                                    ⚡ CRITICAL
                                  </span>
                                ) : obs.flag ? (
                                  <span className="ws-badge badge-urgent">{obs.flag}</span>
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>-</span>
                                )}
                              </td>
                              <td>
                                {obs.previousValue ? (
                                  <div>
                                    <div style={{ fontSize: '12px', color: '#334155' }}>
                                      Prev: <strong>{obs.previousValue}</strong>
                                    </div>
                                    {hasDeltaAlert && (
                                      <div className="ws-alert-delta" style={{ padding: '2px 6px', fontSize: '11px', margin: 0 }}>
                                        ⚠️ Delta Check (Verify)
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontSize: '12px' }}>No history</span>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Critical Value Warning Callout */}
              {detail.observations?.some((o) => o.isCritical) && (
                <div className="ws-alert-critical">
                  <span>🚨</span>
                  <div>
                    <strong>CRITICAL CLINICAL VALUE DETECTED:</strong> One or more parameters cross panic thresholds. Immediate clinician notification / repeat sample validation advised.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Drawer Footer Actions */}
        <div className="ws-drawer-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            className="ws-btn ws-btn-outline"
            onClick={onClose}
          >
            ✕ Close
          </button>
          <button
            type="button"
            className="ws-btn ws-btn-primary"
            onClick={() => onPrintSingle(detail.testId)}
            title="Print Worksheet for this specimen"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            🖨️ PDF / Print Worksheet
          </button>
        </div>
      </div>

      {/* Action Reason Modal (Hold / Repeat / Reject) */}
      {activeModal && (
        <div className="ws-modal-overlay" onClick={handleCloseActionModal}>
          <div
            className="ws-modal-card"
            style={{ width: '480px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ws-modal-header">
              <h3 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>
                {activeModal === 'hold' && '⏸ Put Specimen on Hold'}
                {activeModal === 'repeat' && '↺ Schedule Test for Repeat Analysis'}
                {activeModal === 'reject' && '✕ Mark Specimen as Rejected'}
              </h3>
              <button
                type="button"
                className="ws-btn ws-btn-outline ws-btn-sm"
                onClick={handleCloseActionModal}
              >
                ✕
              </button>
            </div>
            <div className="ws-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label className="ws-filter-label" style={{ display: 'block', marginBottom: '6px' }}>
                  Select Reason *
                </label>
                <select
                  className="ws-filter-select"
                  style={{ width: '100%' }}
                  value={selectedReason}
                  onChange={(e) => setSelectedReason(e.target.value)}
                >
                  <option value="">-- Choose Reason --</option>
                  {activeModal === 'hold' && (
                    <>
                      <option value="Waiting for QC">Waiting for QC</option>
                      <option value="Waiting for Repeat">Waiting for Repeat</option>
                      <option value="Waiting for Sample">Waiting for Sample / Recollection</option>
                      <option value="Instrument Issue">Instrument / Analyzer Issue</option>
                      <option value="Clarification Required">Clarification Required</option>
                      <option value="Doctor Review">Doctor / Pathologist Review</option>
                      <option value="Other">Other</option>
                    </>
                  )}
                  {activeModal === 'repeat' && (
                    <>
                      <option value="QC Issue">QC Issue</option>
                      <option value="Analyzer Error">Analyzer Error / Flag</option>
                      <option value="Dilution Required">Dilution Required</option>
                      <option value="Suspected Result">Suspected Result / Delta Check</option>
                      <option value="Sample Problem">Sample Problem</option>
                      <option value="Technical Error">Technical Error</option>
                      <option value="Other">Other</option>
                    </>
                  )}
                  {activeModal === 'reject' && (
                    <>
                      <option value="Hemolysed">Hemolysed Sample</option>
                      <option value="Clotted">Clotted Sample</option>
                      <option value="Insufficient Volume">Insufficient Volume (QNS)</option>
                      <option value="Wrong Container">Wrong Container / Tube</option>
                      <option value="Leaked">Leaked / Damaged Container</option>
                      <option value="Unlabelled">Unlabelled Specimen</option>
                      <option value="Patient/Sample Mismatch">Patient / Sample Mismatch</option>
                      <option value="Other">Other</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="ws-filter-label" style={{ display: 'block', marginBottom: '6px' }}>
                  Technician Remarks / Notes {selectedReason === 'Other' && '*'}
                </label>
                <textarea
                  className="ws-filter-input"
                  style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                  placeholder="Enter clinical observations or bench notes..."
                  value={actionRemarks}
                  onChange={(e) => setActionRemarks(e.target.value)}
                />
              </div>
            </div>
            <div className="ws-modal-footer">
              <button
                type="button"
                className="ws-btn ws-btn-outline"
                onClick={handleCloseActionModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`ws-btn ${activeModal === 'reject' ? 'ws-btn-danger' : activeModal === 'hold' ? 'ws-btn-amber' : 'ws-btn-primary'}`}
                onClick={handleSubmitAction}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorksheetDetailDrawer;
